import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut
} from 'firebase/auth';
import {
  doc,
  setDoc,
  onSnapshot,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  getCountFromServer
} from 'firebase/firestore';
import { auth, db, callFirebaseFunction } from './firebase';
import { getTopics, getPersonalizedFeed, getFollowingFeed, getHotDebates, getDebatesFeed, getLocalNews, getEvergreenTopics, debateHotness, redeemReferral } from './api';
import { initializePurchases, checkProStatus } from './revenueCat'; 

// Create the Auth context
const AuthContext = createContext();

// Create the AuthProvider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPro, setIsPro] = useState(false); 

  // --- HELPER: Create/Update User in Firestore ---
  const syncUserProfile = async (user, additionalData = {}) => {
    try {
      const userRef = doc(db, 'users', user.uid);

      // FOUNDERS BADGE: Check if this is a new user (has createdAt) and if we're under 250 users
      let foundersBadge = undefined;
      if (additionalData.createdAt) {
        try {
          const usersCollection = collection(db, 'users');
          const countSnapshot = await getCountFromServer(usersCollection);
          const totalUsers = countSnapshot.data().count;
          if (totalUsers < 250) {
            foundersBadge = true;
          }
        } catch (countErr) {
          // Count check failed - skip founders badge
        }
      }

      // FIX: Priority changed to additionalData.handle first to ensure signup handle persists
      await setDoc(userRef, {
        handle: additionalData.handle || user.displayName || 'Anonymous Debater',
        email: user.email,
        photoURL: user.photoURL || `https://api.dicebear.com/9.x/dylan/svg?seed=${user.uid}`,
        lastLogin: new Date(),
        ...(foundersBadge && { foundersBadge: true }),
        ...additionalData
      }, { merge: true });
    } catch (e) {
      // Offline or sync error - silent
    }
  };

  // 1. Listen for Raw Firebase Auth State Changes
  useEffect(() => {
    // FIX: Safety timeout for iOS/Capacitor.
    // If Firebase Auth hangs (common on iOS first launch), force loading to false after 2.5s
    // so the user sees the login screen instead of an infinite spinner.
    const safetyTimeout = setTimeout(() => {
        if (loading) {
            setLoading(false);
        }
    }, 2500);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      clearTimeout(safetyTimeout); // Clear safety timer if auth resolves normally
      
      if (user) {
        setFirebaseUser(user);

        // Verify Admin Status
        try {
            const tokenResult = await user.getIdTokenResult();
            setIsAdmin(!!tokenResult.claims.admin);
        } catch (e) {
            // Token check failed
        }

        // Initialize RevenueCat & Check Pro Status
        try {
            await initializePurchases(user.uid);
            const proStatus = await checkProStatus();
            setIsPro(proStatus);
        } catch (e) {
            // RevCat init failed
        }

      } else {
        setFirebaseUser(null);
        setCurrentUser(null);
        setIsAdmin(false);
        setIsPro(false); 
        setLoading(false);
      }
    });
    
    return () => {
        unsubscribe();
        clearTimeout(safetyTimeout);
    };
  }, []);

  // 2. Listen for Firestore Profile Changes
  useEffect(() => {
    let unsubscribeProfile = () => { };

    if (firebaseUser) {
      const userRef = doc(db, 'users', firebaseUser.uid);

      unsubscribeProfile = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCurrentUser({ ...firebaseUser, ...data });

          // PRIORITY: Trust Firestore for Pro Status if explicitly set
          if (data.isPro === true) {
              setIsPro(true);
          } else if (data.isPro === false) {
              setIsPro(false);
          }
        } else {
          // FIX: Prevent race condition. Only auto-create profile for non-password users (e.g. Google).
          const isPasswordUser = firebaseUser.providerData.some(p => p.providerId === 'password');

          if (!isPasswordUser) {
              const initialStats = {
                xp: 0, level: 1, rank: 'Bronze I',
                wins: 0, winStreak: 0, rating: 1000, totalDebates: 0
              };
              syncUserProfile(firebaseUser, { createdAt: new Date(), stats: initialStats });
          }
          // We set currentUser temporarily so the UI renders
          setCurrentUser({ ...firebaseUser });
        }
        setLoading(false);
      }, (error) => {
        // Handle permission-denied errors (typically from stale auth tokens)
        console.warn('Profile listener error:', error.code);
        if (error.code === 'permission-denied') {
          // Force token refresh and let onAuthStateChanged re-trigger this effect
          firebaseUser.getIdToken(true).catch(() => {});
        }
        setCurrentUser({ ...firebaseUser });
        setLoading(false);
      });
    }

    return () => {
      unsubscribeProfile();
    };
  }, [firebaseUser]);

  // --- AUTH ACTIONS ---

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      throw error;
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      try {
        await callFirebaseFunction('verifyAdminIdentity');
        await userCredential.user.getIdToken(true); 
        const tokenResult = await userCredential.user.getIdTokenResult();
        setIsAdmin(!!tokenResult.claims.admin);
      } catch (e) {
        // Admin check skipped or failed
      }

    } catch (error) {
      throw error;
    }
  };

  const signupWithEmail = async (email, password, handle, referralCode = null) => {
    try {
      // 1. UNIQUE HANDLE CHECK
      const cleanHandle = handle.trim();
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('handle', '==', cleanHandle));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
          throw new Error("This handle is already taken. Please choose another.");
      }

      // 2. Create Auth User
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, {
        displayName: cleanHandle,
        photoURL: `https://api.dicebear.com/9.x/dylan/svg?seed=${cleanHandle}`
      });

      // 3. Sync Profile
      await syncUserProfile(userCredential.user, {
        handle: cleanHandle,
        createdAt: new Date(),
        stats: { xp: 0, level: 1, rank: 'Bronze I', wins: 0, winStreak: 0, rating: 1000, totalDebates: 0 }
      });

      // 4. Process Referral (If provided)
      if (referralCode && referralCode.trim()) {
          try {
              // Wait slightly for Firestore consistency
              await new Promise(r => setTimeout(r, 1000));
              await redeemReferral(referralCode.trim());
          } catch (refError) {
              // Referral failed during signup - don't block
          }
      }

      try {
        await callFirebaseFunction('verifyAdminIdentity');
        await userCredential.user.getIdToken(true); 
        const tokenResult = await userCredential.user.getIdTokenResult();
        setIsAdmin(!!tokenResult.claims.admin);
      } catch (e) {
        // Admin check skipped or failed on signup
      }

    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      // Logout error - silent
    }
  };

  const addXp = async (amount) => {
    // Placeholder - Handled via API/Backend
  };

  const refreshProStatus = async () => {
      if (!firebaseUser) return;

      const status = await checkProStatus();
      setIsPro(status);

      try {
          const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (snap.exists() && snap.data().isPro === true) {
              setIsPro(true);
          }
      } catch(e) { /* silent */ }
  };

  const value = {
    currentUser,
    loading,
    isAdmin,
    isPro, 
    loginWithGoogle,
    loginWithEmail,
    signupWithEmail,
    logout,
    addXp,
    refreshProStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen flex flex-col items-center justify-center bg-white">
          <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-slate-500 text-sm font-medium animate-pulse">Verifying credentials...</p>
        </div>
      ) : (
        <DataProvider>
          {children}
        </DataProvider>
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  return useContext(AuthContext);
};

// --- DATA CONTEXT ---
const DataContext = createContext();

export function DataProvider({ children }) {
  const { currentUser } = useAuth();

  const [feedTopics, setFeedTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [feedType, setFeedType] = useState('trending'); 
  
  // PAGINATION STATE
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const fetchTopics = useCallback(async (isLoadMore = false) => {
    if (!currentUser) return;

    if (!isLoadMore) {
        setLoading(true);
    } else {
        setIsFetchingMore(true);
    }

    // Determine current start point (for load more, use lastDoc)
    const startAfterDoc = isLoadMore ? lastDoc : null;
    const fetchLimit = 30; // Limit requested

    let result = { topics: [], lastVisible: null, hasMore: false };

    if (feedType === 'debates') {
      // Active debates + AI-selected debate-worthy news (controversial stories), hottest first.
      const debates = await getDebatesFeed(40);
      result = { topics: debates, lastVisible: null, hasMore: false };
    } else if (feedType === 'local') {
      // "Local" = the user's STATE. No state chosen yet → empty; Home shows a state picker.
      const st = currentUser.homeState || null;
      result = st ? await getLocalNews({ stateCode: st }) : { topics: [], lastVisible: null, hasMore: false };
    } else if (feedType === 'timeless') {
      // Evergreen debates in their own tab (kept out of the news river).
      const ever = await getEvergreenTopics(40);
      result = { topics: ever, lastVisible: null, hasMore: false };
    } else if (feedType === 'forYou') {
      result = await getPersonalizedFeed(currentUser.uid, fetchLimit, selectedRegion, startAfterDoc);
    } else if (feedType === 'following') {
      result = await getFollowingFeed(currentUser.uid); // Following feed simplified (no rigorous pagination yet)
    } else if (feedType === 'archive') {
      result = await getTopics({ 
          count: fetchLimit, 
          category: selectedCategory, 
          regionCode: selectedRegion, 
          status: 'all',
          lastDoc: startAfterDoc
      });
    } else {
      // TRENDING (Default)
      result = await getTopics({ 
          count: fetchLimit, 
          category: selectedCategory, 
          regionCode: selectedRegion, 
          status: 'active',
          lastDoc: startAfterDoc
      });
    }

    let fetchedTopics = result.topics || [];
    
    // Sort logic (Archive vs Trending)
    const now = new Date();
    const getEndDate = (t) => {
        if (!t.endsAt) return new Date(8640000000000000); 
        if (t.endsAt.toDate) return t.endsAt.toDate();
        return new Date(t.endsAt);
    };

    if (feedType === 'archive') {
        fetchedTopics = fetchedTopics.filter(t => {
            const end = getEndDate(t);
            return end <= now || t.status === 'completed'; 
        });
        fetchedTopics.sort((a, b) => getEndDate(b) - getEndDate(a));
    } else if (feedType === 'trending' || feedType === 'forYou') {
        // Basic sort is handled by Firestore query, but we filter client-side just in case
        fetchedTopics = fetchedTopics.filter(t => {
            const end = getEndDate(t);
            return end > now && t.status !== 'completed';
        });
        if (feedType === 'trending') {
             fetchedTopics.sort((a, b) => (b.postCount || 0) - (a.postCount || 0));
        }
    }

    if (isLoadMore) {
        setFeedTopics(prev => {
            // Dedupe just in case
            const existingIds = new Set(prev.map(t => t.id));
            const newUnique = fetchedTopics.filter(t => !existingIds.has(t.id));
            return [...prev, ...newUnique];
        });
    } else {
        setFeedTopics(fetchedTopics);
    }

    setLastDoc(result.lastVisible);
    // FIX: Use hasMore from API, fallback to checking if we got a full page
    setHasMore(result.hasMore !== undefined ? result.hasMore : fetchedTopics.length === fetchLimit);
    
    setLoading(false);
    setIsFetchingMore(false);

  }, [selectedCategory, selectedRegion, feedType, currentUser, lastDoc]);

  // Wrapper for Initial Load (Reset Pagination)
  const initialFetch = () => {
      setLastDoc(null);
      fetchTopics(false);
  };

  // Wrapper for Load More
  const loadMoreTopics = () => {
      if (!hasMore || isFetchingMore) return;
      fetchTopics(true);
  };

  useEffect(() => {
    // FIX: Added check for currentUser to trigger fetch on first sign-in
    if (currentUser) {
        initialFetch();
    }
  }, [selectedCategory, selectedRegion, feedType, currentUser?.uid]); // Added currentUser.uid dependency

  const value = {
    feedTopics,
    loading,
    feedType,
    setFeedType,
    selectedCategory,
    setSelectedCategory,
    selectedRegion,
    setSelectedRegion,
    forceRefresh: initialFetch,
    loadMoreTopics,
    hasMore,
    isFetchingMore
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => {
  return useContext(DataContext);
};